import { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Table,
  Space,
  Form,
  Input,
  Select,
  Tag,
  Alert,
  message,
  Row,
  Col,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { trpc } from '@/lib/api';
import { NumericInput, RichTextEditor, withDevModalFilePath } from '../common';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/schedule/SchedulePartsSection.tsx');

type SchedulePart = {
  id: string;
  scheduleId: string;
  partId: string;
  expectedQuantity: number;
  consumedQuantity: number;
  consumptionLogId: string | null;
  consumedAt: Date | null;
  consumedBy: string | null;
  notes: string | null;
  part: {
    id: string;
    displayId: string;
    name: string;
    nameKana: string | null;
    partNumber: string | null;
    specifications: string | null;
    manufacturer: string | null;
    unit: string;
    currentStockLevel: number;
    optimalStockLevel: number;
    category: string | null;
  };
};

type Part = {
  id: string;
  displayId: string;
  name: string;
  partNumber: string | null;
  manufacturer: string | null;
  unit: string;
  currentStockLevel: number;
  category: string | null;
};

interface Props {
  scheduleId: string;
  companyId?: string;
  hasTemplate?: boolean;
}

export function SchedulePartsSection({ scheduleId, hasTemplate }: Props) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [consumeModalOpen, setConsumeModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState<SchedulePart | null>(null);
  const [form] = Form.useForm();
  const [consumeForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { modal } = App.useApp();

  // データ取得
  const { data: scheduleParts = [], refetch } = trpc.schedule.getParts.useQuery(
    { scheduleId },
    { enabled: Boolean(scheduleId) }
  );

  // parts.list は認証コンテキストの companyId を使うため、companyId 未取得でも取得する
  const partsListQueryInput = {
    page: 1,
    limit: 500,
  };
  const { data: partsListData } = trpc.parts.list.useQuery(partsListQueryInput, {
    enabled: Boolean(scheduleId),
  });

  const partsList: Part[] = Array.isArray(partsListData?.items)
    ? (partsListData.items as Part[])
    : [];
  const schedulePartRows = useMemo<SchedulePart[]>(
    () => (Array.isArray(scheduleParts) ? (scheduleParts as unknown as SchedulePart[]) : []),
    [scheduleParts]
  );

  // Mutations
  const addPartMutation = trpc.schedule.addPart.useMutation({
    onSuccess: () => {
      messageApi.success('部品・消耗品を追加しました');
      setAddModalOpen(false);
      form.resetFields();
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const updatePartMutation = trpc.schedule.updatePart.useMutation({
    onSuccess: () => {
      messageApi.success('部品・消耗品情報を更新しました');
      setEditModalOpen(false);
      form.resetFields();
      setSelectedPart(null);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const removePartMutation = trpc.schedule.removePart.useMutation({
    onSuccess: () => {
      messageApi.success('部品・消耗品を削除しました');
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const consumePartMutation = trpc.schedule.consumePart.useMutation({
    onSuccess: (result) => {
      messageApi.success(`部品・消耗品消費を記録しました（新在庫: ${result.newBalance}）`);
      setConsumeModalOpen(false);
      consumeForm.resetFields();
      setSelectedPart(null);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  const inheritPartsMutation = trpc.schedule.inheritPartsFromTemplate.useMutation({
    onSuccess: (result) => {
      messageApi.success(result.message);
      void refetch();
    },
    onError: (error) => {
      messageApi.error(`エラー: ${error.message}`);
    },
  });

  // ハンドラー
  const handleAddPart = () => {
    form.resetFields();
    setAddModalOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      addPartMutation.mutate({
        scheduleId,
        partId: values.partId,
        expectedQuantity: values.expectedQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleEdit = (record: SchedulePart) => {
    setSelectedPart(record);
    form.setFieldsValue({
      expectedQuantity: record.expectedQuantity,
      notes: record.notes,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedPart) return;

    try {
      const values = await form.validateFields();
      updatePartMutation.mutate({
        scheduleId,
        partLinkId: selectedPart.id,
        expectedQuantity: values.expectedQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDelete = (record: SchedulePart) => {
    if (record.consumedQuantity > 0) {
      messageApi.warning('消費記録済みの部品・消耗品は削除できません');
      return;
    }

    modal.confirm({
      title: '部品・消耗品を削除しますか？',
      content: `「${record.part.name}」をこのスケジュールから削除します。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        removePartMutation.mutate({
          scheduleId,
          partLinkId: record.id,
        });
      },
    });
  };

  const handleOpenConsumeModal = (record: SchedulePart) => {
    setSelectedPart(record);
    consumeForm.setFieldsValue({
      consumedQuantity: 1,
      notes: null,
    });
    setConsumeModalOpen(true);
  };

  const handleConsumeSubmit = async () => {
    if (!selectedPart) return;

    try {
      const values = await consumeForm.validateFields();
      consumePartMutation.mutate({
        scheduleId,
        partLinkId: selectedPart.id,
        consumedQuantity: values.consumedQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleInheritFromTemplate = () => {
    modal.confirm({
      title: 'テンプレートから部品・消耗品を引き継ぎますか？',
      content: 'テンプレートに設定されている部品・消耗品を、このスケジュールに追加します。',
      okText: '引き継ぐ',
      cancelText: 'キャンセル',
      onOk: () => {
        inheritPartsMutation.mutate({ scheduleId });
      },
    });
  };

  const getStockStatusTag = (current: number, expected: number) => {
    if (current >= expected) {
      return <Tag color="green">十分</Tag>;
    } else if (current >= expected * 0.5) {
      return <Tag color="orange">少ない</Tag>;
    } else if (current > 0) {
      return <Tag color="red">不足</Tag>;
    } else {
      return <Tag color="red">在庫なし</Tag>;
    }
  };

  const calculateAfterStock = () => {
    if (!selectedPart) return '-';
    const consumeQty = consumeForm.getFieldValue('consumedQuantity') || 0;
    const afterStock = selectedPart.part.currentStockLevel - consumeQty;
    return `${afterStock} ${selectedPart.part.unit}`;
  };

  // 在庫不足の警告
  const hasLowStock = schedulePartRows.some(
    (sp) => sp.part.currentStockLevel < sp.expectedQuantity
  );

  const columns: ColumnsType<SchedulePart> = [
    {
      title: '部品・消耗品名',
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <strong>{record.part.name}</strong>
            {record.consumedQuantity > 0 && <Tag color="green">消費済み</Tag>}
          </Space>
          {record.part.partNumber && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              型式: {record.part.partNumber}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '想定数量',
      dataIndex: 'expectedQuantity',
      width: 100,
      render: (qty, record) => `${qty} ${record.part.unit}`,
    },
    {
      title: '消費数量',
      dataIndex: 'consumedQuantity',
      width: 100,
      render: (qty, record) =>
        qty > 0 ? (
          <Text strong>
            {qty} {record.part.unit}
          </Text>
        ) : (
          <Text type="secondary">未消費</Text>
        ),
    },
    {
      title: '現在在庫',
      key: 'stock',
      width: 150,
      render: (_, record) => (
        <Space>
          {record.part.currentStockLevel} {record.part.unit}
          {getStockStatusTag(record.part.currentStockLevel, record.expectedQuantity)}
        </Space>
      ),
    },
    {
      title: '消費記録',
      key: 'consumed',
      width: 150,
      render: (_, record) =>
        record.consumedQuantity > 0 && record.consumedAt ? (
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <Text type="secondary">{dayjs(record.consumedAt).format('MM/DD HH:mm')}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type={record.consumedQuantity === 0 ? 'primary' : 'default'}
            onClick={() => handleOpenConsumeModal(record)}
            disabled={record.part.currentStockLevel === 0}
          >
            {record.consumedQuantity > 0 ? '追加消費' : '消費記録'}
          </Button>
          <Button size="small" onClick={() => handleEdit(record)}>
            編集
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            disabled={record.consumedQuantity > 0}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            使用部品・消耗品（候補）
          </Space>
        }
        extra={
          <Space>
            {hasTemplate && (
              <Button
                icon={<CopyOutlined />}
                onClick={handleInheritFromTemplate}
                loading={inheritPartsMutation.isPending}
              >
                テンプレートから引き継ぎ
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPart}>
              部品・消耗品を追加
            </Button>
          </Space>
        }
      >
        {hasLowStock && (
          <Alert
            type="warning"
            message="在庫が少ない部品・消耗品があります"
            description="作業前に在庫を確認してください"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table<SchedulePart>
          columns={columns}
          dataSource={schedulePartRows}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={false}
        />
      </Card>

      {/* 部品・消耗品追加モーダル */}
      <Modal
        title="部品・消耗品を追加"
        open={addModalOpen}
        onOk={handleAddSubmit}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={addPartMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="部品・消耗品"
            name="partId"
            rules={[{ required: true, message: '部品・消耗品を選択してください' }]}
          >
            <Select
              showSearch
              placeholder="部品・消耗品を選択"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={partsList.map((part) => ({
                label: `${part.name} ${part.partNumber ? `(${part.partNumber})` : ''} - 在庫: ${part.currentStockLevel}${part.unit}`,
                value: part.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="想定使用数量"
            name="expectedQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
            initialValue={1}
            tooltip="実際に使うかどうかは作業時に判断します"
          >
            <NumericInput min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="詳細"
            name="notes"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="詳細を入力（任意）..."
              minHeight={80}
              showPreviewToggle={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 部品・消耗品編集モーダル */}
      <Modal
        title="部品・消耗品情報を編集"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalOpen(false);
          form.resetFields();
          setSelectedPart(null);
        }}
        confirmLoading={updatePartMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="部品・消耗品名">
            <Input disabled value={selectedPart?.part.name} />
          </Form.Item>

          <Form.Item
            label="想定使用数量"
            name="expectedQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter={selectedPart?.part.unit} />
          </Form.Item>

          <Form.Item
            label="詳細"
            name="notes"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="詳細を入力（任意）..."
              minHeight={80}
              showPreviewToggle={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 部品・消耗品消費記録モーダル */}
      <Modal
        title={
          selectedPart?.consumedQuantity && selectedPart.consumedQuantity > 0
            ? '部品・消耗品追加消費記録'
            : '部品・消耗品消費記録'
        }
        open={consumeModalOpen}
        onOk={handleConsumeSubmit}
        onCancel={() => {
          setConsumeModalOpen(false);
          consumeForm.resetFields();
          setSelectedPart(null);
        }}
        confirmLoading={consumePartMutation.isPending}
        okText="消費記録"
        cancelText="キャンセル"
        width={700}
      >
        <Form form={consumeForm} layout="vertical">
          <Form.Item label="部品・消耗品名">
            <Input disabled value={selectedPart?.part.name} />
          </Form.Item>

          <Form.Item label="型式">
            <Input disabled value={selectedPart?.part.partNumber || '-'} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="想定数量">
                <Input
                  disabled
                  value={
                    selectedPart
                      ? `${selectedPart.expectedQuantity} ${selectedPart.part.unit}`
                      : '-'
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="既に消費済み">
                <Input
                  disabled
                  value={
                    selectedPart
                      ? `${selectedPart.consumedQuantity || 0} ${selectedPart.part.unit}`
                      : '-'
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="今回の消費数量"
            name="consumedQuantity"
            rules={[
              { required: true, message: '消費数量を入力してください' },
              { type: 'number', min: 1, message: '1以上の数値を入力してください' },
            ]}
          >
            <NumericInput
              min={1}
              max={selectedPart?.part.currentStockLevel}
              style={{ width: '100%' }}
              addonAfter={selectedPart?.part.unit}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="現在在庫">
                <Input
                  disabled
                  value={
                    selectedPart
                      ? `${selectedPart.part.currentStockLevel} ${selectedPart.part.unit}`
                      : '-'
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="消費後在庫">
                <Input disabled value={calculateAfterStock()} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="詳細"
            name="notes"
            getValueFromEvent={(markdown: string) => markdown}
            getValueProps={(value) => ({ value: value || '' })}
          >
            <RichTextEditor
              placeholder="消費の理由や詳細を記録（任意）..."
              minHeight={80}
              showPreviewToggle={false}
            />
          </Form.Item>

          <Alert
            type="info"
            message="部品・消耗品消費について"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>この操作により在庫が即座に減少します</li>
                <li>部品・消耗品消費ログと在庫ログに記録されます</li>
                <li>追加で消費する場合は、再度「追加消費」ボタンを押してください</li>
              </ul>
            }
            showIcon
          />
        </Form>
      </Modal>
    </>
  );
}
